import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { sportsApi } from '@/services/api';
import { SportsAcademy } from '@/types';
import { CatalogCard } from '@/components/public/CatalogCard';
import { usePublicSearchStore } from '@/store/publicSearchStore';
import { CategoryCardCarousel, groupItemsByCategory } from '@/components/public/CategoryCardCarousel';

export const SportsPage = () => {
  const [academies, setAcademies] = useState<SportsAcademy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const searchQuery = usePublicSearchStore((state) => state.queries.sports);
  const trimmedSearchQuery = searchQuery.trim();
  const deferredSearchQuery = useDeferredValue(trimmedSearchQuery);
  const normalizedSearchQuery = deferredSearchQuery.toLowerCase();
  const visibleAcademies = normalizedSearchQuery
    ? academies.filter((academy) => academy.name.toLowerCase().includes(normalizedSearchQuery))
    : academies;
  const academyGroups = useMemo(() => groupItemsByCategory(visibleAcademies), [visibleAcademies]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      setIsLoading(true);
      setError('');
      try {
        const data = await sportsApi.getAllSports({ signal: controller.signal });
        setAcademies(data);
      } catch (fetchError) {
        if ((fetchError as Error).name !== 'AbortError') {
          setError('Sports academies are not available right now. Please try again soon.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    void fetchData();
    return () => controller.abort();
  }, []);

  return (
    <section className="courses-page min-h-full">
      <div className="courses-shell">
        {trimmedSearchQuery ? (
          <p className="courses-search-note">
            Showing results for <span className="text-slate-950">&quot;{searchQuery}&quot;</span>
          </p>
        ) : null}

        {isLoading ? (
          <div className="courses-grid">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
              <div key={item} className="courses-skeleton" />
            ))}
          </div>
        ) : error ? (
          <div className="courses-empty-state">{error}</div>
        ) : visibleAcademies.length === 0 ? (
          <div className="courses-empty-state">
            {trimmedSearchQuery ? 'No sports academies matched your search.' : 'No sports academies found.'}
          </div>
        ) : (
          <div className="space-y-8">
            {academyGroups.map((group) => (
              <CategoryCardCarousel
                key={group.id}
                title={group.name}
                itemCount={group.items.length}
                itemLabel="listing"
              >
                {group.items.map((academy) => (
                  <CatalogCard
                    key={academy.id}
                    href={`/sports/${academy.slug}`}
                    imageUrl={academy.coverImageUrl}
                    imageAlt={academy.name}
                    title={academy.name}
                    ctaLabel="Learn More"
                    isFree={academy.isFree}
                    originalPrice={academy.startingPriceText}
                    videoUrl={academy.previewVideoUrl}
                  />
                ))}
              </CategoryCardCarousel>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
